import { FC, useState } from 'react'
import { useTranslation } from 'next-i18next/pages'
import { useForm, FieldError } from 'react-hook-form'
import type {
    GiftCardProduct as GiftCardProductInterface,
    CustomizableOptionInterface
} from '@pylot-data/pylotschema'
import { ProductStockStatus } from '@pylot-data/enums/ProductStockStatus'
import { Button } from '@corratech/pylot-ui'
import { AmountDropdown, OtherAmountEnum } from './AmountDropdown'
import {
    useAddToCart,
    isGiftCardMixCartResult
} from '@corratech/pylot-cart-manager'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import {
    getIsValidEmailAddress,
    validateEmailForForm,
    validateEmailsMatch
} from '@corratech/pylot-utils'
import { getMapFieldWithUid } from './getMapFieldWithUid'
import formStyles from '@commonStyles/Forms.module.scss'
import cn from 'classnames'
import s from '@pagestyles/Giftcard.module.scss'
import formInputStyle from '@components/common/FormInput/FormInput.module.scss'
import { OpenAmount } from './OpenAmount'
import { blockDefaultEvent } from './blockDefaultEvent'

export interface GiftCardProductInputArgs {
    sender_name: string
    sender_email: string
    receiver_name: string
    receiver_email: string
    receiver_email_confirm: string
    gift_message: string
    gift_amounts: string | OtherAmountEnum.Other
    gift_open_amounts: number
}

export type Props = {
    product: GiftCardProductInterface
}

export type GiftProductOptionsValueUid = {
    value: { uid: string }
} & CustomizableOptionInterface

export const GiftCardProduct: FC<Props> = ({ product }) => {
    const [quantity, setQuantity] = useState(1)
    const { t } = useTranslation(['common', 'pdp'])
    const { addToCart, isAdding } = useAddToCart()
    const { openSidebar, openCartToast } = useUI()
    const [messageCount, setMessageCount] = useState(0)
    const {
        stock_status,
        allow_message,
        message_max_length,
        gift_card_options,
        giftcard_amounts,
        allow_open_amount
    } = product
    const {
        reset,
        register,
        handleSubmit,
        formState: { errors },
        unregister,
        control,
        watch
    } = useForm<GiftCardProductInputArgs>({
        mode: 'onChange'
    })
    const isOutOfStock = stock_status === ProductStockStatus.OutOfStock

    // const fixedAmountUid =
    //     giftcard_amounts && giftcard_amounts?.length >= 1
    //         ? giftcard_amounts[0]?.uid
    //         : ''

    const onSubmit = async (formInput: GiftCardProductInputArgs) => {
        const hasSelectedAmount =
            formInput.gift_amounts &&
            formInput.gift_amounts !== OtherAmountEnum.Other

        const data = await addToCart(
            [
                {
                    sku: product.sku!,
                    entered_options: getMapFieldWithUid({
                        giftCardOptions:
                            gift_card_options as GiftProductOptionsValueUid[],
                        formInput
                    }),
                    ...(hasSelectedAmount && {
                        selected_options: [formInput.gift_amounts]
                    }),
                    // selected_options: [fixedAmountUid!],
                    quantity: 1
                }
            ],
            [product]
        )

        const errorMessage =
            data?.errors?.[0]?.message ?? data?.user_errors?.[0]?.message ?? ''

        const hasError = Boolean(
            data?.errors?.length || data?.user_errors?.length
        )

        if (isGiftCardMixCartResult(data)) {
            return
        }

        if (!hasError) {
            openSidebar()
            setTimeout(() => {
                openCartToast(
                    t('cart|{{name}} was added to cart', {
                        name: product.name
                    })
                )
                setMessageCount(0)
            }, 300)
            reset({
                ...(giftcard_amounts?.length &&
                    allow_open_amount && {
                        gift_amounts: OtherAmountEnum.Other
                    })
            })
        } else {
            openCartToast(errorMessage, ToastType.Warning)
        }

        reset({
            ...(giftcard_amounts?.length &&
                allow_open_amount && {
                    gift_amounts: OtherAmountEnum.Other
                })
        })
    }

    const getInputClassnames = (error?: FieldError) =>
        cn(
            formInputStyle.root,
            `appearance-none w-full border-b ${
                error ? 'border-alert-danger' : 'border-p-7'
            } px-0 mb-2 focus:shadow-none focus:outline-none focus-visible:border-b focus-visible:shadow-none`,
            formInputStyle.formInput,
            s['giftcard-input']
        )

    return (
        <div className={cn(s['gift-card-container'], formStyles.root)}>
            <AmountDropdown
                register={register}
                product={product}
                errors={errors}
            />
            <OpenAmount
                control={control}
                register={register}
                product={product}
                errors={errors}
                unregister={unregister}
            />
            <p className="font-normal mb-5 mt-7">
                {t('Completa los siguientes datos:')}
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className={s['giftcard-form-grid']}>
                    <div className="form-group">
                        <input
                            type="text"
                            {...register('sender_name', { required: true })}
                            className={getInputClassnames(errors.sender_name)}
                            id="sender_name"
                            placeholder={t('Tu nombre *')}
                        />
                        {errors.sender_name && (
                            <span className="error">
                                {t('authErrors|Required')}
                            </span>
                        )}
                    </div>
                    <div className="form-group">
                        <input
                            type="text"
                            {...register('sender_email', {
                                required: 'This field is required',
                                validate: getIsValidEmailAddress
                            })}
                            className={getInputClassnames(errors.sender_email)}
                            id="sender_email"
                            placeholder={t('Tu correo *')}
                        />
                        {errors.sender_email &&
                            (errors.sender_email.type == 'required' ? (
                                <span className="error">
                                    <span className="error">
                                        {t('authErrors|Required')}
                                    </span>
                                </span>
                            ) : (
                                <span className="error">
                                    <span className="error">
                                        {t('authErrors|Invalid Email')}
                                    </span>
                                </span>
                            ))}
                    </div>
                </div>
                <div className={s['giftcard-form-grid']}>
                    <div className="form-group">
                        <input
                            type="text"
                            {...register('receiver_name', {
                                required: true
                            })}
                            className={getInputClassnames(errors.receiver_name)}
                            id="receiver_name"
                            placeholder={t('Nombre del destinatario *')}
                        />
                        {errors.receiver_name && (
                            <span className="error">
                                {t('authErrors|Required')}
                            </span>
                        )}
                    </div>
                    <div className="form-group">
                        <input
                            type="text"
                            {...register('receiver_email', {
                                required: 'This field is required',
                                validate: validateEmailForForm('es')
                            })}
                            className={getInputClassnames(
                                errors.receiver_email
                            )}
                            id="receiver_email"
                            placeholder={t('Correo del destinatario *')}
                            onCopy={blockDefaultEvent}
                            onPaste={blockDefaultEvent}
                            onCut={blockDefaultEvent}
                            onDragStart={blockDefaultEvent}
                        />
                        {errors.receiver_email && (
                            <span className="error">
                                {errors.receiver_email.type === 'required'
                                    ? t('authErrors|Required')
                                    : t('authErrors|Invalid Email')}
                            </span>
                        )}
                    </div>

                    <div className="form-group">
                        <input
                            type="text"
                            {...register('receiver_email_confirm', {
                                required: 'This field is required',
                                validate: validateEmailsMatch(watch)
                            })}
                            className={getInputClassnames(
                                errors.receiver_email_confirm
                            )}
                            id="receiver_email_confirm"
                            placeholder={t(
                                'pdp||giftCard|Confirm recipient email*'
                            )}
                            onCopy={blockDefaultEvent}
                            onPaste={blockDefaultEvent}
                            onCut={blockDefaultEvent}
                            onDragStart={blockDefaultEvent}
                        />
                        {errors.receiver_email_confirm && (
                            <span className="error">
                                {errors.receiver_email_confirm.type ===
                                'required'
                                    ? t('authErrors|Required')
                                    : errors.receiver_email_confirm.message}
                            </span>
                        )}
                    </div>
                </div>
                {allow_message && (
                    <div className="form-group">
                        <div className={s['giftcard-textarea-container']}>
                            <textarea
                                {...register('gift_message', {
                                    maxLength: message_max_length!,
                                    required: true
                                })}
                                className={cn(
                                    s['giftcard-textarea'],
                                    s['giftcard-input'],
                                    errors.gift_message && 'border-alert-danger'
                                )}
                                id="gift_message"
                                maxLength={message_max_length || 200}
                                onChange={(e) =>
                                    setMessageCount(e.target.value.length)
                                }
                                placeholder={t('pdp||giftCard|Write a message')}
                            />
                            <div className={s['giftcard-textarea-counter']}>
                                {messageCount}/{message_max_length}
                            </div>
                        </div>
                        {errors.gift_message && (
                            <span className="error">
                                <span className="error">
                                    {t('authErrors|Required')}
                                </span>
                            </span>
                        )}
                    </div>
                )}
                <div className={s['qty-selector']}>
                    <div className={s['buttons-container']}>
                        <Button
                            loading={isAdding}
                            disabled={isAdding || isOutOfStock}
                            onClick={handleSubmit(onSubmit)}
                            className={s['giftcard-add-to-cart']}
                            type="submit"
                            variant="primary"
                        >
                            {t('pdp||giftCard|{{ label }}', {
                                label: isOutOfStock
                                    ? t('pdp||giftCard|Out of stock')
                                    : t('pdp||giftCard|Add to cart')
                            })}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    )
}
